import { BellRing, CheckCircle2, Clock3, Send, ShieldAlert, Smartphone } from 'lucide-react';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import { EmptyState, LoadingRows, PageHeader, StatusBadge } from '@/components/ui';
import { apiFetch } from '@/lib/client-api';

interface NotificationRecord {
  id: string;
  title: string;
  body: string;
  status: 'PENDING' | 'SENT' | 'FAILED';
  fcmMessageId: string | null;
  errorMessage: string | null;
  createdAt: string;
  sentAt: string | null;
  createdBy: { id: string; email: string };
}

interface NotificationResult {
  data: NotificationRecord[];
  total: number;
}

export default function NotificationsPage() {
  const [result, setResult] = useState<NotificationResult>();
  const [title, setTitle] = useState('Futsaly');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      setResult(await apiFetch<NotificationResult>('/admin/notifications?pageSize=50'));
    } catch (cause) {
      setError((cause as Error).message);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => { void load(); }, 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!window.confirm('Send this notification now to every subscribed Futsaly mobile device?')) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      await apiFetch('/admin/notifications', {
        method: 'POST',
        body: JSON.stringify({ title, body }),
      });
      setBody('');
      setSuccess('The push notification was accepted by Firebase for delivery.');
      await load();
    } catch (cause) {
      setError((cause as Error).message);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return <div className="page">
    <PageHeader title="Mobile notifications" description="Broadcast an important message to every mobile device subscribed to Futsaly." />
    <div className="notification-admin-grid">
      <form className="card notification-composer" onSubmit={submit}>
        <div className="notification-composer-head"><span><BellRing/></span><div><h2>New broadcast</h2><p>Notifications can appear while the mobile app is backgrounded or closed.</p></div></div>
        {error && <div className="error">{error}</div>}
        {success && <div className="notification-success"><CheckCircle2/>{success}</div>}
        <label className="form-field"><span>Notification title</span><input className="field" value={title} onChange={(event) => setTitle(event.target.value)} minLength={2} maxLength={80} required/><small>{title.length}/80 characters</small></label>
        <label className="form-field"><span>Message</span><textarea className="textarea notification-message" value={body} onChange={(event) => setBody(event.target.value)} minLength={2} maxLength={500} placeholder="Write the message mobile users should receive…" required/><small>{body.length}/500 characters</small></label>
        <div className="notification-audience"><Smartphone/><div><strong>All mobile users</strong><span>Sent to the secure Futsaly Firebase topic.</span></div></div>
        <button className="button notification-send" disabled={busy || body.trim().length < 2}><Send size={17}/>{busy ? 'Sending…' : 'Review and send now'}</button>
      </form>
      <aside className="card notification-guidance"><ShieldAlert/><h3>Before sending</h3><ul><li>Keep the message short and useful.</li><li>Do not send passwords or private user information.</li><li>The final send action asks for confirmation.</li></ul><p>Delivery requires Firebase credentials on the API server and Firebase configuration in the mobile build.</p></aside>
    </div>
    <section className="notification-history">
      <div className="notification-history-head"><div><h2>Broadcast history</h2><p>{result?.total ?? 0} notification{result?.total === 1 ? '' : 's'} recorded</p></div><button className="button secondary" onClick={() => void load()}>Refresh</button></div>
      {!result ? <LoadingRows/> : result.data.length === 0 ? <EmptyState message="No mobile notifications have been sent yet."/> : <div className="notification-history-list">{result.data.map((item) => <article className="card notification-history-item" key={item.id}><div className={`notification-history-icon ${item.status.toLowerCase()}`}>{item.status === 'SENT' ? <CheckCircle2/> : item.status === 'FAILED' ? <ShieldAlert/> : <Clock3/>}</div><div><div className="notification-history-title"><strong>{item.title}</strong><StatusBadge status={item.status}/></div><p>{item.body}</p><small>{new Date(item.createdAt).toLocaleString()} · {item.createdBy.email}</small>{item.errorMessage && <span className="notification-delivery-error">{item.errorMessage}</span>}</div></article>)}</div>}
    </section>
  </div>;
}
